import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import './SystemHealthDashboard.css';
import { getIpLogs } from '../../helper/IpLogHelper';
import { ROLE } from '../../enum/role';
import { ACTIVE_DEVICE, TIMELINE_CONST } from '../../common/constant/common-constants';
import {
  formatMachineSerial,
  getHealthScoreMeta,
  parseMachineRecord,
} from '../../utils/machinePayloadUtils';

const FILTER = {
  ALL: 'all',
  ONLINE: 'online',
  OK: 'ok',
  DEGRADED: 'degraded',
  CRITICAL: 'critical',
  UNKNOWN: 'unknown',
};

/** Online = reported within the last minute. */
const ONLINE_WINDOW_SEC = 60;

const isOnline = (updatedAt) =>
  Boolean(updatedAt) && moment(updatedAt).isAfter(moment().subtract(ONLINE_WINDOW_SEC, 'seconds'));

const isDeviceMatch = (machineUDI, activeDevice) => {
  if (!machineUDI) return false;
  const prefix = machineUDI.split('-')[0]?.[0];
  if (activeDevice === ACTIVE_DEVICE.V730i) return prefix === 'V';
  if (activeDevice === ACTIVE_DEVICE.NX5) return prefix === 'P';
  return true;
};

const resolveHealthScore = (record) => {
  if (!record) return null;
  if (record.overallHealthScore != null) return String(record.overallHealthScore).toLowerCase();
  if (record.healthScore != null) return String(record.healthScore).toLowerCase();

  const parsed = parseMachineRecord(record);
  const health = parsed?.systemHealth;
  if (!health) return null;

  const score = health.health_score ?? health.healthScore ?? health.overallHealthScore;
  return score != null ? String(score).toLowerCase() : null;
};

const resolveSomStale = (record) => {
  if (record?.somStale === true) return true;
  const parsed = parseMachineRecord(record);
  return Boolean(parsed?.systemHealth?.somStale || parsed?.systemHealth?.som?.stale);
};

const SystemHealthDashboard = ({ userRole, activeDevice }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER.ALL);

  const fetchLogs = useCallback((isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    getIpLogs()
      .then((res) => {
        setLogs(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        setError('Unable to load machine health list.');
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const cards = useMemo(() => {
    const query = search.trim().toLowerCase();

    return logs
      .filter((item) => isDeviceMatch(item.machineUDI, activeDevice))
      .map((item) => {
        const score = resolveHealthScore(item);
        const meta = getHealthScoreMeta(score);
        const serial = formatMachineSerial(item.machineUDI, activeDevice);
        return {
          machineUDI: item.machineUDI,
          serial,
          score: score || 'unknown',
          meta,
          somStale: resolveSomStale(item),
          online: isOnline(item.updatedAt),
          updatedAt: item.updatedAt,
        };
      })
      .filter((item) => {
        if (statusFilter === FILTER.ALL) return true;
        if (statusFilter === FILTER.ONLINE) return item.online;
        return item.score === statusFilter;
      })
      .filter((item) => {
        if (!query) return true;
        return (
          item.machineUDI?.toLowerCase().includes(query) ||
          item.serial?.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }, [logs, activeDevice, search, statusFilter]);

  const stats = useMemo(() => {
    const deviceLogs = logs.filter((item) => isDeviceMatch(item.machineUDI, activeDevice));
    const scores = deviceLogs.map((item) => resolveHealthScore(item) || 'unknown');
    return {
      total: deviceLogs.length,
      online: deviceLogs.filter((item) => isOnline(item.updatedAt)).length,
      ok: scores.filter((s) => s === 'ok').length,
      degraded: scores.filter((s) => s === 'degraded').length,
      critical: scores.filter((s) => s === 'critical').length,
      unknown: scores.filter((s) => s === 'unknown').length,
    };
  }, [logs, activeDevice]);

  if (!(userRole === ROLE.ADMIN || userRole === ROLE.PRODUCTION)) {
    return (
      <div className="sh-unauthorized">
        <h1>Unauthorized Access</h1>
        <p>You do not have the necessary permissions to access this page.</p>
      </div>
    );
  }

  const openHealth = (machineUDI) => {
    navigate(`/ip-log/health/${encodeURIComponent(machineUDI)}`);
  };

  return (
    <div className="sh-page">
      <div className="sh-container">
        <header className="sh-header">
          <div>
            <p className="sh-eyebrow">Fleet Overview</p>
            <h1 className="sh-title">System Health</h1>
          </div>
          <div className="sh-header-actions">
            <input
              type="search"
              className="sh-search"
              placeholder="Search machine number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search machines"
            />
            <button
              type="button"
              className="sh-refresh-btn"
              onClick={() => fetchLogs(true)}
              disabled={loading || refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </header>

        <section className="sh-stats-grid">
          {[
            { key: FILTER.ALL, label: 'Total', value: stats.total },
            { key: FILTER.ONLINE, label: 'Online', value: stats.online, tone: 'online' },
            { key: FILTER.OK, label: 'Healthy', value: stats.ok, tone: 'ok' },
            { key: FILTER.DEGRADED, label: 'Degraded', value: stats.degraded, tone: 'degraded' },
            { key: FILTER.CRITICAL, label: 'Critical', value: stats.critical, tone: 'critical' },
            { key: FILTER.UNKNOWN, label: 'Unknown', value: stats.unknown, tone: 'unknown' },
          ].map((stat) => (
            <button
              key={stat.key}
              type="button"
              className={`sh-stat-card ${stat.tone ? `sh-stat-card--${stat.tone}` : ''} ${statusFilter === stat.key ? 'sh-stat-card--active' : ''}`}
              onClick={() => setStatusFilter(stat.key)}
            >
              <span className="sh-stat-label">{stat.label}</span>
              <span className="sh-stat-value">{stat.value}</span>
            </button>
          ))}
        </section>

        {loading && (
          <div className="sh-state">
            <div className="sh-spinner" />
            <p>Loading system health…</p>
          </div>
        )}

        {!loading && error && (
          <div className="sh-state sh-state--error">
            <p>{error}</p>
            <button type="button" onClick={() => fetchLogs()}>Try again</button>
          </div>
        )}

        {!loading && !error && cards.length === 0 && (
          <div className="sh-state">
            <p>No machines match this filter.</p>
          </div>
        )}

        {!loading && !error && cards.length > 0 && (
          <section className="sh-card-grid">
            {cards.map((card) => (
              <button
                key={card.machineUDI}
                type="button"
                className={`sh-machine-card sh-machine-card--${card.score}`}
                onClick={() => openHealth(card.machineUDI)}
              >
                <div className="sh-machine-card-top">
                  <span className="sh-machine-serial">{card.serial}</span>
                  <div className="sh-machine-badges">
                    <span
                      className={`sh-status-dot ${card.online ? 'sh-status-dot--online' : 'sh-status-dot--offline'}`}
                      title={card.online ? 'Online (active in last 1 min)' : 'Offline (no update in last 1 min)'}
                      aria-label={card.online ? 'Online' : 'Offline'}
                    />
                    <span
                      className={`sh-health-symbol sh-health-symbol--${card.score}`}
                      title={card.score === 'ok' ? 'Healthy' : card.meta.label}
                      aria-label={card.score === 'ok' ? 'Healthy' : card.meta.label}
                    >
                      {card.score === 'ok' && (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      {card.score === 'degraded' && (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 3l10 18H2L12 3Z" fill="currentColor" />
                          <path d="M12 10v5M12 17.5h.01" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                      {card.score === 'critical' && (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M7.5 3h9l4.5 4.5v9L16.5 21h-9L3 16.5v-9L7.5 3Z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                          <path d="M9 9l6 6M15 9l-6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      )}
                      {(card.score === 'unknown' || !['ok', 'degraded', 'critical'].includes(card.score)) && (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
                          <path d="M8 12h8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>
                  </div>
                </div>
                <p className="sh-machine-udi" title={card.machineUDI}>{card.machineUDI}</p>
                <div className="sh-machine-card-foot">
                  {card.somStale && (
                    <span className="sh-chip sh-chip--stale">SOM stale</span>
                  )}
                  <span className="sh-machine-updated">
                    {card.updatedAt
                      ? moment(card.updatedAt).format(`${TIMELINE_CONST.DATE_FORMAT} ${TIMELINE_CONST.TIME_FORMAT}`)
                      : '—'}
                  </span>
                </div>
              </button>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userRole: state.userDetails.userRole,
  activeDevice: state.setactiveDevice.device,
});

export default connect(mapStateToProps)(SystemHealthDashboard);
